import React from "react";
import { enqueueSnackbar } from "notistack";
import { ApolloQueryResult } from "@apollo/client";
import { DataFromTablesByDatasetIdQuery } from "graphql/types/graphql";

interface SelectedCube {
  cubeId: string;
  name?: string;
}

interface UseCardClickHandlerProps {
  openedDatasetIds: string[];
  setOpenedDatasetIds: React.Dispatch<React.SetStateAction<string[]>>;
  setTableOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedColumns: React.Dispatch<
    React.SetStateAction<Record<string, { selectedColumns: string[]; joinColumn: string | null; joinWith?: string }>>
  >;
  datasetColumns: Record<string, string[]>;
  setDatasetColumns: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  getDataFromTables: (options: {
    variables: { cubeName: string; datasetId: string; includeAlreadyMerged: boolean };
  }) => Promise<ApolloQueryResult<DataFromTablesByDatasetIdQuery>>;
  selectedCube: SelectedCube;
}

export const useCardClickHandler = ({
  openedDatasetIds,
  setOpenedDatasetIds,
  setTableOrder,
  setSelectedColumns,
  datasetColumns,
  setDatasetColumns,
  getDataFromTables,
  selectedCube,
}: UseCardClickHandlerProps): ((datasetId: string) => Promise<void>) => {
  const FILTERED_COLUMNS = [
    "DATASETID",
    "SOURCEFILEID",
    "CUBEID",
    "originalFileName",
    "datasetId",
    "cubeId",
    "WASALREADYMERGEDFROMDATASETS",
  ];

  const handleCardClick = async (datasetId: string): Promise<void> => {
    if (openedDatasetIds.includes(datasetId)) {
      setOpenedDatasetIds((prev) => prev.filter((id) => id !== datasetId));
      setTableOrder((prev) => prev.filter((id) => id !== datasetId));

      setSelectedColumns((prev) => {
        const { [datasetId]: _, ...remaining } = prev;

        const updatedRemaining = Object.fromEntries(
          Object.entries(remaining).map(([key, value]) => [
            key,
            {
              ...value,
              joinWith: value.joinWith === datasetId ? undefined : value.joinWith,
            },
          ])
        );

        return updatedRemaining;
      });
    } else {
      setOpenedDatasetIds((prev) => [...prev, datasetId]);
      setTableOrder((prev) => [...prev, datasetId]);

      if (!datasetColumns[datasetId]) {
        try {
          const response = await getDataFromTables({
            variables: { cubeName: selectedCube.name, datasetId, includeAlreadyMerged: true },
          });

          if (response?.data?.dataFromTablesByDatasetId?.columnNames) {
            const columns = response.data.dataFromTablesByDatasetId.columnNames.filter(
              (col: string) => !FILTERED_COLUMNS.includes(col)
            );

            setDatasetColumns((prev) => ({
              ...prev,
              [datasetId]: Array.from(new Set(columns)),
            }));
          }
        } catch (error) {
          enqueueSnackbar(`Failed to load columns for dataset ${datasetId}`, { variant: "error" });
        }
      }
    }
  };

  return handleCardClick;
};
